import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Select } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { faUserShield } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
import { useLazyGetRolesByOrganizationQuery } from "../../../redux/roles/roleAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// INTERFACES
import { RootState } from "../../../app/store";
import { FormInputSelectRoleInterface } from "../../../interfaces/Global.interface";
// FLATBUFFER DISPLAY
import { formatUuidT } from "../../../flatbuffers/display";
// COMPONENTS
import DrawerRowDivider from "../drawer/DrawerRowDivider";
// MASTER-ORG ROLE MARKERS — same star/shield pair used by the Roles table,
// SelectRolesLight, and the wizard's user "Role" picker, so a LiftNet role
// reads the same way here.
import { isMasterOrgId } from "../../../utils/masterOrganization";
import { isMasterRoleName } from "../../../utils/isMasterAdmin";
import MasterRoleStar from "../../../components/roles/MasterRoleStar";
import TenantRoleShield from "../../../components/roles/TenantRoleShield";
import RoleMarkerLegend from "../../../components/roles/RoleMarkerLegend";

function FormInputSelectRole({
  orgId,
  inputValue,
  inputOnChange,
  inputVariableName,
}: FormInputSelectRoleInterface) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const { Option } = Select;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [roles, setRoles] = useState<any[]>([]);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme, userLogged } = useSelector((state: RootState) => state.home);
  // Only a user who themselves belongs to the master organization may see
  // the MASTER ADMIN role at all here — same guard RolesTable.tsx and
  // StepUsersRoles.tsx use for the Roles table / Add User "Role" picker.
  const isLoggedUserInMasterOrg =
    userLogged?.organizationId === process.env.REACT_APP_MASTER_ORG_ID;

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetRoles, { data }] = useLazyGetRolesByOrganizationQuery();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const normalizeRolesResponse = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  };

  const getRoleId = (role: any): string =>
    formatUuidT(role?.id ?? role?.roleId ?? role?.role_id ?? "");

  /**
   * The text an operator reads for a role, resolved the same way every other
   * role picker resolves it (RolesCreateRoleForm's "Role Base" options, the
   * wizard's Users step). Three things this has to get right, each of which
   * this component previously got wrong and rendered a BLANK option for:
   *
   *  - `GET /roles/` always answers in FlatBuffers (LiftCloud's
   *    writePaginatedRoles has no JSON branch), and the Object API names the
   *    field `displayName`. Reading only snake_case `display_name` meant a
   *    role's display name was never honoured here.
   *  - `??` does not fall through an EMPTY string, only null/undefined, so a
   *    display name of "" resolved to "" instead of falling back to the name.
   *  - With no final fallback, ANY empty result renders an option with no
   *    text — a row the operator can select but cannot read.
   *
   * The id tail is what makes a blank option structurally impossible: it is
   * ugly, but it is always non-empty and always identifies the row.
   */
  const getRoleLabel = (role: any): string => {
    // First NON-BLANK candidate wins — not the first non-nullish one. `??`
    // stops at an empty string, which is exactly how a display name of ""
    // (11 of 35 rows in the reference database) shadowed a perfectly good
    // role name and produced an unreadable option.
    const candidates = [role?.displayName, role?.display_name, role?.name];
    const label = candidates.map((value) => String(value ?? "").trim()).find(Boolean);
    return label || getRoleId(role);
  };

  const launchGetRoles = async () => {
    const normalizedOrgId = formatUuidT(orgId as any);
    if (!normalizedOrgId) {
      setRoles([]);
      return;
    }
    const token = await GETJwtToken();
    triggerGetRoles({ token, orgId: normalizedOrgId });
  };
  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    setRoles(normalizeRolesResponse(data));
  }, [data]);

  useEffect(() => {
    launchGetRoles();
  }, [orgId]);

  const normalizedInputValue = useMemo(() => formatUuidT(inputValue as any), [inputValue]);
  const selectedRoleExists = useMemo(
    () => roles.some((role: any) => getRoleId(role) === normalizedInputValue),
    [roles, normalizedInputValue],
  );

  // FRONTEND-ONLY visual filter (same trade-off as RolesTable.tsx and
  // StepUsersRoles.tsx): a user outside the master organization never sees
  // the MASTER ADMIN role in this picker.
  const visibleRoles = useMemo(
    () =>
      isLoggedUserInMasterOrg ? roles : roles.filter((role: any) => !isMasterRoleName(role.name)),
    [roles, isLoggedUserInMasterOrg],
  );

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      {/** ************************************************* */}
      {/** COMPONENT DRAWER DIVIDER */}
      <DrawerRowDivider icon={faUserShield} title={t("userManagement.role")} />
      {/** ************************************************* */}
      <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
        <div className="container">
          <div className="row">
            <div className="col-md-12 mt-3">
              <div className="generalStyles__inputFlexNameLarge">
                <span className="generalStyles__inputFlexRequired">*</span>
                <span className="drawer__text">{t("userManagement.role")}</span>
              </div>
              <div
                className="generalStyles__width100"
                style={{
                  marginTop: "9px",
                }}
              >
                <Select
                  disabled={roles.length === 0}
                  showSearch
                  // `label` holds the searchable plain name; the children
                  // render the star/shield + name. antd can only substring-
                  // search a string — filtering on the star/shield-bearing
                  // node would never match what the operator types (same
                  // pattern as SelectRolesLight).
                  filterOption={(input: string, option: any) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  className="generalStyles__width100"
                  size="large"
                  value={inputValue}
                  onChange={(event: any) => {
                    inputOnChange(event, inputVariableName);
                  }}
                >
                  {normalizedInputValue && !selectedRoleExists && (
                    <Option
                      key={normalizedInputValue}
                      value={normalizedInputValue}
                      label={normalizedInputValue}
                    >
                      {normalizedInputValue}
                    </Option>
                  )}
                  {visibleRoles.map((role: any) => {
                    const isMaster = isMasterOrgId(formatUuidT(role?.orgId ?? role?.org_id));
                    const name = getRoleLabel(role);
                    return (
                      <Option key={getRoleId(role)} value={getRoleId(role)} label={name}>
                        {/* Same star/shield pair as the Roles table and
                            SelectRolesLight: the master star for a
                            LiftNet/MASTER role, the tenant shield for
                            everything else — every option carries one
                            marker. */}
                        <MasterRoleStar show={isMaster} />
                        <TenantRoleShield show={!isMaster} />
                        {name}
                      </Option>
                    );
                  })}
                </Select>
              </div>
              {/* Explains the star/shield each option carries. Without it the
                  markers are decoration an operator has to guess at. */}
              <div style={{ marginTop: 16 }}>
                <RoleMarkerLegend />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FormInputSelectRole;
